// 入札一覧の CSV 出力 (通常入札 [[buildNormalBidCsv]]・ブロック入札 [[buildBlockBidCsvFiles]]) が
// 共通で出すセルの組み立て (`bidding/spot-bidding.md` §2 機能詳細 9)。
//
// 通常入札とブロック入札は、 CSV インポートの取込と同じ列に出す約束 (取り込むと同じ札に戻る) が
// 同じなので、 発注主体区分・売買・価格・排出係数紐付けと JEPX自由記述欄の出し方を 1 箇所に置く。 どちらかだけを
// 直して、 出した CSV が取込にかけ直した時に別の札になる状態を作らないため。
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import {
  BLOCK_BID_CSV_ISSUER_TYPE_VALUE,
  BLOCK_BID_CSV_SIDE_VALUE,
  type BidCsvLocalizedValue,
} from '~/models/bidCsvHeader'
import { formatYenPerKwh } from '~/models/feeAutoConsiderPreview'
import { neutralizeFormula } from '~/pages/SpotBidding/models/csvCell'
import { parseEmissionComment } from '~/pages/SpotBidding/models/emissionLinking'

/**
 * `発注主体区分` 列の値 (spec spot-bid-normal-csv-import.md §2 / spot-bid-block-csv-import.md §2)。
 * CSV で指定できない区分 (DR 等) と未設定 (eBalance 以外から取り込んだ札) は undefined にする
 * (呼び出し側が空セルにし、 取込側で必須エラーになる。 別の区分で黙って復元されることはない。
 * 機能詳細 9)。
 */
export const bidCsvIssuerTypeValue = (
  issuerType: LicenseType | undefined,
): BidCsvLocalizedValue | undefined => {
  switch (issuerType) {
    case LicenseType.RETAIL:
      return BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail
    case LicenseType.GENERATION:
      return BLOCK_BID_CSV_ISSUER_TYPE_VALUE.generation
    case LicenseType.SELF_WHEELING:
      return BLOCK_BID_CSV_ISSUER_TYPE_VALUE.selfWheeling
    default:
      return undefined
  }
}

/**
 * `売買` 列の値 (spec の CSV の値。 表示用の i18n ラベルは使わない)。
 *
 * SELL 以外を一律 買 に倒すのは意図的: proto enum の既定値 (`SIDE_UNSPECIFIED`) で
 * 空セルを出すと、 取込側の 売買 検証 (AC-SPOT-003a / AC-DSB-005) でファイル全行が拒否され、
 * 削除前の控えが復元手段として使えなくなる (issue #6633)。
 */
export const bidCsvSideValue = (direction: Side): BidCsvLocalizedValue =>
  direction === Side.SELL
    ? BLOCK_BID_CSV_SIDE_VALUE.sell
    : BLOCK_BID_CSV_SIDE_VALUE.buy

/** 価格列の逆算を backend のプレビュー RPC に問い合わせる 1 件 (保存済みの指値の札の売買と入札価格)。 */
export type BidCsvPriceQuery = { direction: Side; orderPriceYenPerKwh: string }

/**
 * 売買は `売買` 列 ([[bidCsvSideValue]]) と同じく SELL 以外を買いに倒す。 取り込み直すと買いとして算出されるので
 * 逆算も買いで問い合わせる (プレビュー RPC は売買の未設定を受け付けない)。
 */
const bidCsvPriceSide = (direction: Side): Side.SELL | Side.BUY =>
  direction === Side.SELL ? Side.SELL : Side.BUY

/**
 * 問い合わせと組み立てが同じ鍵で引く。 通常入札は Decimal 文字列、 ブロック入札は数値から作った文字列を
 * 渡すので、 小数 2 桁に揃えて同じ札の価格を同じ鍵にする。
 */
export const bidCsvPriceQueryKey = (q: BidCsvPriceQuery): string =>
  `${String(bidCsvPriceSide(q.direction))}:${formatYenPerKwh(q.orderPriceYenPerKwh)}`

/**
 * 価格列に何を出すか。 押した後のプレビュー応答で決める ([[useBidCsvExportContext]])。
 *
 * - `orderPrice`: 手数料自動考慮が OFF。 保存済みの入札価格
 * - `desiredPrice`: ON。 backend が保存済みの入札価格から現在の手数料単価で逆算した希望価格 (AC-BSP-004b)。
 *   鍵は [[bidCsvPriceQueryKey]]
 */
export type BidCsvPriceSource =
  | { kind: 'orderPrice' }
  | {
      kind: 'desiredPrice'
      desiredPriceYenPerKwhByQuery: ReadonlyMap<string, string>
    }

/**
 * `価格 (円/kWh)` 列の値 (小数 2 桁)。 取り込むと同じ入札価格に戻るように出す (spec 機能詳細 9)。
 * 逆算値が JEPX の値域を外れても丸めない (再アップロード時に取込の検証が行エラーにする)。
 *
 * ON で逆算値の無い札は throw する。 入札価格で埋めると ON の組織に手数料を戻していない列が出て、
 * 取り込むと手数料が二重にかかる。
 */
export const bidCsvPriceCell = (
  orderPriceYenPerKwh: string,
  direction: Side,
  price: BidCsvPriceSource,
): string => {
  if (price.kind === 'orderPrice') return formatYenPerKwh(orderPriceYenPerKwh)
  const desired = price.desiredPriceYenPerKwhByQuery.get(
    bidCsvPriceQueryKey({ direction, orderPriceYenPerKwh }),
  )
  if (desired === undefined) {
    throw new Error(
      `bidCsvPriceCell: no desired price for ${String(direction)}:${orderPriceYenPerKwh}`,
    )
  }
  return formatYenPerKwh(desired)
}

/** プレビュー RPC の `desired_price_queries` の 1 回あたりの上限 (proto の `max_items`)。 */
const DESIRED_PRICE_QUERIES_MAX = 500

/** 問い合わせを重複 (同じ鍵) を除いて、 1 回の RPC に載せられる件数ごとに分ける。 */
export const bidCsvDesiredPriceRequests = (
  queries: readonly BidCsvPriceQuery[],
): BidCsvPriceQuery[][] => {
  const unique = new Map<string, BidCsvPriceQuery>()
  for (const q of queries) {
    const key = bidCsvPriceQueryKey(q)
    if (!unique.has(key)) {
      unique.set(key, {
        direction: bidCsvPriceSide(q.direction),
        orderPriceYenPerKwh: formatYenPerKwh(q.orderPriceYenPerKwh),
      })
    }
  }
  const all = [...unique.values()]
  const chunks: BidCsvPriceQuery[][] = []
  for (let i = 0; i < all.length; i += DESIRED_PRICE_QUERIES_MAX) {
    chunks.push(all.slice(i, i + DESIRED_PRICE_QUERIES_MAX))
  }
  return chunks
}

/**
 * `排出係数紐付け` と `JEPX自由記述欄` の 2 列 (この並びで返す)。
 *
 * - 排出係数紐付けありの札 (自社の判別コメントを認識できた札): `1` + 判別コメントの英字 1 文字
 *   (判別コメントの後ろに書いた自由記述は出さない)
 * - 判別コメントを認識できない札: 紐付けを空欄にし、 保存済みの JEPX自由記述欄をそのまま
 *   自由記述として戻す (JEPX へ送る note は同じ値になる)
 *
 * 自由記述は数式インジェクション対策をかける (CWE-1236)。 取込はセルの値を変換しないので、
 * 先頭が = + - @ の値に足したアポストロフィは、 取り込み直すと値の先頭に残る。
 */
export const bidCsvEmissionCells = (
  memoJepx: string | undefined,
  retailerId: string | undefined,
): [linking: string, freeText: string] => {
  const emission = parseEmissionComment(memoJepx, retailerId)
  return emission.linked
    ? ['1', emission.alpha]
    : ['', neutralizeFormula(memoJepx ?? '')]
}
